import Vue from 'vue';
import CryptoJS from 'crypto-js';
import App from './App.vue';
import './registerServiceWorker';
import router from './router';
import store from './store';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/fonts/icons.js';
import './services/GlobalVariables';
import DrawerLayout from 'vue-drawer-layout';
import VueVirtualScroller from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import VueToggles from 'vue-toggles';
import Loading from 'vue-loading-overlay';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import 'vue-loading-overlay/dist/vue-loading.css';
import * as Sentry from "@sentry/vue";

// Register global components
Vue.component('loading', Loading);
Vue.component('VueToggles', VueToggles);
Vue.component('v-select', vSelect);

Vue.use(VueVirtualScroller);
Vue.use(DrawerLayout);

Vue.config.productionTip = false;

/**
 * Sentry event hook that strips known PII fields from event payloads
 * before they leave the renderer. Defence-in-depth on top of
 * `dataCollection.userInfo: false` — covers event tags, extra, contexts,
 * user, breadcrumbs, and request bodies.
 *
 * Keys are matched case-insensitively against object keys one level deep
 * (event root) and recursively in `extra` / `contexts` / `user` / `request`.
 *
 * @param {Object} event  Sentry event object (mutated in place).
 * @param {Object} _hint  Sentry hint (unused, required by API).
 * @returns {Object}      The (possibly redacted) event, or null to drop.
 */
function redactPII(event, _hint) {
    const sensitive = new Set(['pin', 'password', 'creditcard', 'token']);
    const scrub = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        for (const key of Object.keys(obj)) {
            if (sensitive.has(key.toLowerCase())) {
                obj[key] = '[redacted]';
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                scrub(obj[key]);
            }
        }
    };
    scrub(event);
    return event;
}

// DEPRECATED: events from installs <0.3.0 still go to dsr-app (the legacy
// Sentry project slug). This init writes to the new `lytsoft-dsr` slug —
// the old `dsr-app` will be archived once we are confident no installs
// pre-0.3.0 are still reporting. See `docs/exec-plans/completed/2026-07-22-sentry-vue2-integration.md`.
Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [
        // Performance: traces page loads + route changes
        Sentry.browserTracingIntegration({ router }),
        // Session Replay: 10% of sessions, 100% on error
        Sentry.replayIntegration(),
    ],
    // PII controls — opt out of user identity by default (POS app, PII-sensitive)
    dataCollection: {
        userInfo: false,
    },
    // Sampling
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Redact known PII fields before send
    beforeSend: redactPII,
    // Release tag — correlates to git SHA in CI
    release: process.env.VUE_APP_SENTRY_RELEASE,
    // Environment tag — staging vs prod
    environment: process.env.VUE_APP_SENTRY_ENVIRONMENT || 'production',
    // Forward console logs
    enableLogs: true,
});

Vue.prototype.$crypto = CryptoJS;
new Vue({
    router,
    store,
    render: h => h(App),
}).$mount('#app');
